import React, { Ref, useCallback, useImperativeHandle } from 'react';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useFixedLengthNumberInput } from '../../hooks/useFixedLengthNumberInput/useFixedLengthNumberInput';
import { useModalOpened } from '../../hooks/useModalOpened/useModalOpened';
import { useMultiElementIsFocused } from '../../hooks/useMultiElementIsFocused/useMultiElementIsFocused';
import { InputLayout, InputLayoutProps } from '../../input/InputLayout/InputLayout';
import sharedStyles from '../../input/sharedStyles.module.css';
import { IconDeprecated } from '../../misc/IconDeprecated/IconDeprecated';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { TimeClockPickerModal } from '../TimeClockPickerModal/TimeClockPickerModal';
import styles from './TimeInput.module.css';
export type TimeInputProps = PropsWithoutChildren<Omit<InputLayoutProps, 'onInput' | 'onChange' | 'onInputCapture' | 'onChangeCapture' | 'ref'>> & {
  readonly hours: number | null;
  readonly minutes: number | null;
  readonly readOnly?: boolean;
  readonly onInput: (hours: number | null, minutes: number | null) => void;
  readonly ref?: Ref<TimeInputRef>;
};
export type TimeInputRef = {
  focus: () => void;
};
export const TimeInput = React.memo<TimeInputProps>(props => {
  const {
    hours: hoursProp,
    minutes: minutesProp,
    onInput,
    readOnly,
    ref,
    ...remainingProps
  } = prepareProps({
    props,
    ignoreChildren: true
  });
  useImperativeHandle(ref, () => ({
    focus: () => {
      focusHoursInput();
    }
  }));
  const {
    close: closeTimePicker,
    open: openTimePicker,
    modalProps: timePickerProps
  } = useModalOpened({
    closeOnBackDropClick: true
  });
  const acceptNewValues = useEventCallback((hours: number | null, minutes: number | null) => {
    const hoursLimit = 24;
    const limitedHours = hours != null ? Math.min(hours, hoursLimit) : null;
    const minutesLimit = limitedHours !== 24 ? 60 : 0;
    const limitedMinutes = minutes != null ? Math.min(minutes, minutesLimit) : null;
    if (limitedHours !== hoursProp || limitedMinutes !== minutesProp) {
      onInput(limitedHours, limitedMinutes);
    }
  });
  const {
    isFocused: anyInputIsFocused,
    focusHandlerFactory,
    blurHandlerFactory
  } = useMultiElementIsFocused({
    onBlur: () => {
      acceptNewValues(hoursBufferedValue, minutesBufferedValue);
    }
  });
  const {
    inputProps: minutesInputProps,
    bufferedValue: minutesBufferedValue,
    focus: focusMinutesInput
  } = useFixedLengthNumberInput({
    value: minutesProp,
    anyInputIsFocused,
    valueLength: 2,
    onFocus: focusHandlerFactory('MinutesInput'),
    onBlur: blurHandlerFactory('MinutesInput')
  });
  const handleHoursInputValueLengthReached = useCallback(() => {
    focusMinutesInput();
  }, [focusMinutesInput]);
  const {
    inputProps: hoursInputProps,
    bufferedValue: hoursBufferedValue,
    focus: focusHoursInput
  } = useFixedLengthNumberInput({
    value: hoursProp,
    anyInputIsFocused,
    valueLength: 2,
    onFocus: focusHandlerFactory('HoursInput'),
    onBlur: blurHandlerFactory('HoursInput'),
    onValueLengthReached: handleHoursInputValueLengthReached
  });
  return <>
      <InputLayout {...remainingProps}>
        {() => <div className={styles.inputsWrapper}>
            <input type="text" className={classes(sharedStyles.inputBaseStyle, styles.input)} {...hoursInputProps} readOnly={readOnly} data-testid="TimeInput.HoursInput"></input>
            <div className={styles.hoursMinutesSeparator}>:</div>
            <input type="text" className={classes(sharedStyles.inputBaseStyle, styles.input)} {...minutesInputProps} readOnly={readOnly} data-testid="TimeInput.MinutesInput"></input>
            <button type="button" className={styles.timePickerButton} onClick={openTimePicker} data-testid="TimeInput.OpenTimePickerModalButton">
              <IconDeprecated name="clock"></IconDeprecated>
            </button>
          </div>}
      </InputLayout>
      <TimeClockPickerModal hours={hoursProp} minutes={minutesProp} onValueChanged={acceptNewValues} onClose={closeTimePicker} data-testid="TimeInput.TimePickerModal" {...timePickerProps}></TimeClockPickerModal>
    </>;
});